/**
 * Laying a hand out as an overlapping arc instead of a row of tiles.
 *
 * Pure and separately tested, because this is where the arithmetic mistakes live:
 * an off-by-one in the centre index makes the whole fan lean, and an overlap that
 * does not scale with the count pushes a thirteen-card hand off a phone screen.
 */

export interface FannedCard {
  /** Degrees; negative on the left of centre. */
  rotate: number;
  /** Pixels down; the ends of the arc sit lower than the middle. */
  translateY: number;
  /**
   * Pixels of negative left margin, i.e. how much this card slides over its
   * neighbour. Zero for the first card.
   */
  overlap: number;
  /** Ascending, so each card sits on top of the one to its left. */
  zIndex: number;
}

/** Widest the fan may be, in pixels. A 390px phone minus the panel padding. */
const AVAILABLE_WIDTH = 320;

/** Degrees between neighbouring cards, before the count tightens it. */
const MAX_STEP_DEGREES = 4;

/** How far the outermost card drops, in pixels. */
const MAX_ARC_LIFT = 10;

/**
 * @param count  Cards in the hand.
 * @param cardWidth  Rendered width of one card in pixels.
 */
export function fanLayout(count: number, cardWidth = 56): FannedCard[] {
  if (count <= 0) return [];

  const centre = (count - 1) / 2;

  // Tighten the spread as the hand grows, so a big hand is a fan and not a
  // windmill.
  const step = count <= 1 ? 0 : Math.min(MAX_STEP_DEGREES, 24 / count);

  /*
   * Overlap needed to fit: laid out edge to edge the hand is
   * `count * cardWidth`, and every card after the first can give back
   * `overlap` pixels. Never negative (that would add gaps) and never so much
   * that the visible sliver of a card stops being tappable.
   */
  const excess = count * cardWidth - AVAILABLE_WIDTH;
  const needed = count > 1 ? excess / (count - 1) : 0;
  const overlap = Math.max(0, Math.min(needed, cardWidth * 0.62));

  return Array.from({ length: count }, (_, index) => {
    const offset = index - centre;
    // Quadratic, normalised on the outermost card, so the arc looks the same
    // whether the hand holds three cards or thirteen.
    const edge = centre === 0 ? 0 : (offset / centre) ** 2;

    return {
      rotate: offset * step,
      translateY: edge * MAX_ARC_LIFT,
      overlap: index === 0 ? 0 : overlap,
      zIndex: index + 1,
    };
  });
}

/** Total rendered width of a fan, for tests and for centring. */
export function fanWidth(layout: FannedCard[], cardWidth = 56): number {
  if (layout.length === 0) return 0;
  return layout.reduce((total, card) => total + cardWidth - card.overlap, 0);
}
